/**
 * @fileoverview Local-first service for the system inspection-program catalog
 *   (issue #737 Phase 6, plan §11.5 step 58).
 * @module system-web/services/systemInspectionProgramService
 *
 * Responsibilities:
 * - Read the canonical `systemInspectionPrograms` catalog from Dexie
 *   (`system_inspection_programs` — synced pull-only mirror).
 * - Route all writes through system-api (`apiFetch`) and put the response
 *   into Dexie for immediate UI feedback; the API triggers tenant
 *   propagation after each write.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb)
 * - ../apiClient (dynamic import for writes — online mutations)
 *
 * Architecture:
 * - Server-authoritative pattern (push is blocked in
 *   SYSTEM_PUSH_ALLOWED_COLLECTIONS for `systemInspectionPrograms`).
 * - READS: Dexie/liveQuery-safe pure reads.
 * - WRITES: apiClient -> system-api -> Dexie put (immediate UX); the
 *   canonical replay then propagates the definition to every tenant's
 *   `inspectionPrograms` collection.
 * - `propagateToTenants` is the manual "Propagate to All Tenants" trigger —
 *   it returns the per-catalog replay counts and does not write Dexie.
 *
 * Notes:
 * - NEVER write to Dexie inside a liveQuery observed function.
 * - The `hidden`/`memberCount` flags are tenant-side concerns — never
 *   sent from this surface.
 *
 * @localFirst
 * Source of Truth: hybrid — Dexie for reads, system-api for writes.
 * Write Flow: apiClient -> Dexie put; sync pull reconciles eventually.
 * Sync Strategy: read-only pull.
 * Conflict Resolution: server-authoritative.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';

// ---------------------------------------------------------------------------
// Contracts — mirror api/packages/db/src/inspectionProgramReplay.ts
// ---------------------------------------------------------------------------

/**
 * @contract SystemInspectionProgramSlot
 * Purpose: One schedulable slot inside a program division.
 * Fields:  _id (stable slot key), label, targetMonth (1-12).
 * Rules:   Slots are system-managed; tenants cannot restructure them.
 * Sync:    Replayed verbatim into tenant `inspectionPrograms[].slots`.
 */
export interface SystemInspectionProgramSlot {
  _id: string;
  label: string;
  targetMonth: number;
}

/**
 * @contract SystemInspectionProgram
 * Purpose: One canonical predefined inspection program in the system catalog.
 * Fields:  programKey (stable natural key), name, description, reoccurrence,
 *          division, slotCount, slots, applicableCategories, active.
 * Rules:   `programKey` unique; `active: false` propagates tenant deactivation.
 * Sync:    Server-authoritative; synced read-only to Dexie
 *          `system_inspection_programs`. Push is blocked.
 */
export interface SystemInspectionProgram {
  _id: string;
  programKey: string;
  name: string;
  description?: string;
  reoccurrence: 'annual' | 'biennial' | 'triennial';
  division: 'monthly' | 'quarterly' | 'semiannual' | 'annual';
  slotCount: number;
  slots: SystemInspectionProgramSlot[];
  applicableCategories: string[];
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  _deleted?: boolean;
}

export interface SystemInspectionProgramPatch {
  name?: string;
  description?: string;
  reoccurrence?: string;
  division?: string;
  slots?: SystemInspectionProgramSlot[];
  applicableCategories?: string[];
}

/** Per-catalog replay counts returned by the propagate trigger. */
export interface SchedulingPropagationSummary {
  programs?: { succeeded: number; failed: number; totalTenants: number } | null;
  ruleCatalog?: { succeeded: number; failed: number; totalTenants: number } | null;
}

// ---------------------------------------------------------------------------
// Dexie reads (liveQuery-safe — pure reads, never write)
// ---------------------------------------------------------------------------

/**
 * @process ListSystemInspectionPrograms
 * Steps:           Return all non-deleted catalog rows from Dexie, sorted by
 *                  reoccurrence/division/name for a stable admin grid.
 * Inputs:          none.
 * Outputs:         SystemInspectionProgram[].
 * Failure Handling:Dexie read errors propagate to the caller.
 * Idempotency:     REQUIRED — pure read; safe inside liveQuery.
 */
export async function listSystemInspectionPrograms(): Promise<SystemInspectionProgram[]> {
  const db = await getSystemDb();
  const rows = (await db.system_inspection_programs.toArray()) as unknown as SystemInspectionProgram[];
  return rows
    .filter((p) => !p._deleted)
    .sort(
      (a, b) =>
        (a.reoccurrence ?? '').localeCompare(b.reoccurrence ?? '') ||
        (a.division ?? '').localeCompare(b.division ?? '') ||
        (a.name ?? '').localeCompare(b.name ?? ''),
    );
}

export async function getSystemInspectionProgram(id: string): Promise<SystemInspectionProgram | null> {
  const db = await getSystemDb();
  const row = (await db.system_inspection_programs.get(id)) as unknown as SystemInspectionProgram | undefined;
  return row && !row._deleted ? row : null;
}

// ---------------------------------------------------------------------------
// Writes — apiClient -> system-api (server propagates to tenants) -> Dexie put
// ---------------------------------------------------------------------------

/**
 * @process UpdateSystemInspectionProgram
 * Steps:           PUT the patch to system-api; on success put the returned
 *                  program into Dexie so the grid updates immediately.
 * Inputs:          id — system doc id; patch — whitelisted definition fields.
 * Outputs:         Updated SystemInspectionProgram.
 * Failure Handling:Throws on non-2xx; Dexie untouched on failure.
 * Idempotency:     REQUIRED — same patch converges.
 */
export async function updateSystemInspectionProgram(
  id: string,
  patch: SystemInspectionProgramPatch,
): Promise<SystemInspectionProgram> {
  const { api } = await import('../apiClient');
  const result = (await api.inspectionPrograms.update(id, patch)) as {
    program: SystemInspectionProgram;
    propagation?: unknown;
  };
  const db = await getSystemDb();
  await db.system_inspection_programs.put(result.program as unknown as Record<string, unknown>);
  wakeLocalWrite('system_inspection_programs');
  return result.program;
}

/**
 * @process SetSystemInspectionProgramActive
 * Steps:           PATCH /:id/active; put the returned program into Dexie.
 * Inputs:          id; active flag.
 * Outputs:         Updated SystemInspectionProgram.
 * Failure Handling:Throws on non-2xx; Dexie untouched on failure.
 * Idempotency:     REQUIRED.
 */
export async function setSystemInspectionProgramActive(
  id: string,
  active: boolean,
): Promise<SystemInspectionProgram> {
  const { api } = await import('../apiClient');
  const result = (await api.inspectionPrograms.setActive(id, active)) as {
    program: SystemInspectionProgram;
    propagation?: unknown;
  };
  const db = await getSystemDb();
  await db.system_inspection_programs.put(result.program as unknown as Record<string, unknown>);
  wakeLocalWrite('system_inspection_programs');
  return result.program;
}

/**
 * @process CreateSystemInspectionProgram
 * Steps:           POST to system-api; put the returned program into Dexie.
 * Inputs:          Program definition (name/reoccurrence/division/slots).
 * Outputs:         Created SystemInspectionProgram.
 * Failure Handling:Throws on non-2xx; Dexie untouched on failure.
 * Idempotency:     NOT REQUIRED — creates a new catalog entry per call.
 */
export async function createSystemInspectionProgram(
  input: SystemInspectionProgramPatch & { programKey?: string },
): Promise<SystemInspectionProgram> {
  const { api } = await import('../apiClient');
  const result = (await api.inspectionPrograms.create(input)) as {
    program: SystemInspectionProgram;
    propagation?: unknown;
  };
  const db = await getSystemDb();
  await db.system_inspection_programs.put(result.program as unknown as Record<string, unknown>);
  wakeLocalWrite('system_inspection_programs');
  return result.program;
}

/**
 * @process PropagateSchedulingCatalogs
 * Steps:           POST /api/inspection-programs/propagate — replays the
 *                  system catalogs to every tenant.
 * Inputs:          scope — 'inspectionPrograms' | 'scheduleRuleCatalog' | 'all'.
 * Outputs:         Propagation counts per catalog.
 * Failure Handling:Throws on non-2xx. No Dexie write — pull reconciles.
 * Idempotency:     REQUIRED — replays converge.
 */
export async function propagateSchedulingCatalogs(
  scope: 'inspectionPrograms' | 'scheduleRuleCatalog' | 'all' = 'all',
): Promise<SchedulingPropagationSummary> {
  const { api } = await import('../apiClient');
  const result = (await api.inspectionPrograms.propagate(scope)) as {
    propagation: SchedulingPropagationSummary;
  };
  return result.propagation;
}
